// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Blank for Interactive Game
//
// Include description of object & useful info here, please!

import type {
  Accidental,
  LetterName,
  NoteName,
} from 'shared/definitions/musicTheory/notesAndAccidentals';
import InteractivePrototype from './interactivePrototype';
import type { InteractiveOptions } from './interactivePrototype';

import utils from '../utils';
import config from '../config';
import Note from '../uTheory.Notation/note';
import Interval from '../uTheory.Notation/interval';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import { toHTML } from '../uTheory.Font/font';

interface EnharmonicEquivalenceOptions extends InteractiveOptions {
  lowestNote?: NoteName;
  highestNote?: NoteName;
  levels?: string | number;
}

class EnharmonicEquivalence extends InteractivePrototype {
  highlightColor = config.Colors.highlighted;

  answerTextDivs: HTMLDivElement[] = [];

  answerTextDiv: HTMLDivElement;

  feedbackDiv: HTMLDivElement;

  note: Note;

  maxQuestions: number;

  declare options: EnharmonicEquivalenceOptions;

  answerLetter: LetterName;

  answerAccidental?: Accidental;

  questionPart: number;

  /** The note for each enharmonic answer */
  answerPartNotes: Note[];

  declare questions: { note: Note; skills: number[] }[];

  private timeoutID?: number;

  constructor(displayDiv: HTMLDivElement, options: InteractiveOptions) {
    super(displayDiv, options);
    this.targetSpeedFromSkills = EnharmonicEquivalence.registry.skills;
    const settings = {
      instructions: true,
      piano: {
        lowestNote: 'B3',
        highestNote: 'C5',
        listeners: false,
        ariaLive: 'alert',
        ariaNoteDescriptionType: 'location',
      },
      letters: { letterButtons: true, accidentalButtons: true, checkButton: 'Next' },
      checkMark: {
        rightAnswerLength: 1,
        rightAnswerInOutRatio: 0.7,
        wrongAnswerLength: 1.0,
        wrongAnswerInOutRatio: 0.3,
      },
      player: {},
    };

    this.layout = {
      letters: { percent: 25 },
    };

    this.init(displayDiv, options, settings);
  }

  // Overriding these methods / properties:
  init(displayDiv: HTMLDivElement, options: InteractiveOptions, settings): Promise<any> {
    options.maxQuestions = options.maxQuestions || 5;

    // copy our settings onto the prototype settings:
    utils.merge(this.settings, settings);

    // now call the prototype's init function:
    return super.init(displayDiv, options);

    // this.answerTextDiv.style.width = this.piano.width + "px";
  }

  initializeCustomElements(): void {
    //      this.layout.customElements = 1;
    this.createDiv('answerTextDiv', { margin: '0 auto' });
    this.displayDiv.removeChild(this.answerTextDiv);
    this.displayDiv.insertBefore(this.answerTextDiv, this.pianoDiv);

    this.layout.customDiv = {
      div: this.answerTextDiv,
      visible: true,
      percent: 10,
      callback: (height) => this.sizeTextDiv(height, this.answerTextDiv),
    };

    for (let i = 0; i < 3; i++) {
      const options = {};
      this.answerTextDivs[i] = EnharmonicEquivalence.createFloaterDiv(options, this.answerTextDiv);
      const question = i + 1;
      this.answerTextDivs[i].innerHTML = `${question}: `;
    }
    this.answerTextDivs[0].style.backgroundColor = this.highlightColor;

    this.feedbackDiv = EnharmonicEquivalence.createFloaterDiv(
      { cssFloat: 'none', clear: 'both', width: '100%' },
      this.answerTextDiv
    );
    this.feedbackDiv.className = 'utheory-gcr-error';

    this.pianoDiv.style.clear = 'both';
  }

  static createFloaterDiv(options = {}, parentDiv): HTMLDivElement {
    //      var width = this.piano.width/3;
    const styleOptions = { width: '33.3333%', cssFloat: 'left', fontSize: '1.5em' };
    utils.merge(styleOptions, options);
    const div = document.createElement('div');
    utils.merge(div.style, styleOptions);
    //      this[divName].style.width = styleOptions.width;
    parentDiv.appendChild(div);
    div.setAttribute('aria-live', 'polite');
    return div;
  }

  generateQuestions(): void {
    this.questions = [];
    this.questionNumber = 0;
    this.questionPrompt =
      typeof this.options.questionPrompt !== 'undefined'
        ? this.options.questionPrompt
        : 'What are three names for the highlighted note?';

    this.note = new Note();

    if (typeof this.options.drill !== 'undefined' && this.options.drill) {
      console.log('Drilling');
      const generator = new QuestionGenerator();
      this.options.questions = generator.enharmonic_equivalence(this.options.maxQuestions);
    }

    // Setup each question:
    this.options.questions.forEach((question) => {
      this.questions.push({ note: new Note(question), skills: [18] });
    }, this);
    this.maxQuestions = this.questions.length;
  }

  randomQuestionGenerator(): Note[] {
    /* Levels:
      1: C-G
      2: All white notes
      9: Sharps
      10: Flats
      11: Double Sharps
      12: Double Flats
    */
    const lowestNote = new Note(this.options.lowestNote);
    const highestNote = new Note(this.options.highestNote);

    let acceptableWhiteNotes: string[] = [];
    const levelsArray = `${this.options.levels}`.split('/');
    if (levelsArray.indexOf('1') !== -1) {
      acceptableWhiteNotes = acceptableWhiteNotes.concat(['C', 'D', 'E', 'F', 'G']);
    }
    if (levelsArray.indexOf('2') !== -1) {
      acceptableWhiteNotes = acceptableWhiteNotes.concat(['A', 'B']);
    }

    let whiteNotes: Note[] = [];
    const range = Interval.getGenericInterval(lowestNote, highestNote);
    for (let i = 0; i < range; i++) {
      const note = Interval.getNoteAtGenericInterval(lowestNote, i);
      if (acceptableWhiteNotes.indexOf(note.letter)) whiteNotes.push(note);
    }

    while (whiteNotes.length < this.options.maxQuestions) {
      whiteNotes = whiteNotes.concat(whiteNotes);
    }
    utils.shuffle(whiteNotes);

    // Check for repetitions and eliminate them:
    for (let i = 1; i < whiteNotes.length; i++) {
      if (whiteNotes[i].equals(whiteNotes[i - 1])) {
        whiteNotes.splice(i, 1);
      }
      if (whiteNotes.length < this.maxQuestions) {
        whiteNotes = whiteNotes.concat(whiteNotes);
      }
    }

    whiteNotes = whiteNotes.slice(0, this.options.maxQuestions);
    return whiteNotes;
  }

  addListeners(): void {
    this.letterPanel.addLetterCallback(this.letter, this);
    this.letterPanel.addAccidentalCallback(this.accidental, this);
    this.letterPanel.addCheckCallback(this.check, this);
  }

  static calculateSkill(note: Note): number {
    switch (note.accidental) {
      case 'bb':
        return 13;
      case 'b':
        return 11;
      case '':
        if (note.letter === 'A' || note.letter === 'B') return 3;
        return 2;
      case '#':
        return 10;
      case 'x':
        return 12;
    }
    return -1;
  }

  newQuestion(): void {
    super.newQuestion();
    this.answerPartNotes = [];
    this.questionPart = 0;
    //      this.questionNumber++;
    if (this.questionNumber > 0) {
      window.setTimeout(() => this.newQuestionDelayer(), 400);
    } else this.newQuestionDelayer();
  }

  newQuestionDelayer(): void {
    this.piano.clearColors();
    this.note.setName(this.options.questions[this.questionNumber]);
    this.piano.colorNote(this.note.getMidiNumber());
    for (let i = 0; i < 3; i++) {
      const question = i + 1;
      this.answerTextDivs[i].innerHTML = `${question}: `;
    }
    this.answerTextDivs[0].style.backgroundColor = this.highlightColor;
  }

  letter(letter: LetterName): void {
    this.answerLetter = letter;
    this.updateAnswerDiv();
  }

  accidental(accidental): void {
    this.answerAccidental = accidental;
    this.updateAnswerDiv();
  }

  updateAnswerDiv(): void {
    const questNum = this.questionPart + 1;
    let html = `${questNum}: `;
    if (typeof this.answerLetter !== 'undefined') html += this.answerLetter;
    if (typeof this.answerAccidental !== 'undefined') {
      html += toHTML('accidental', this.answerAccidental || '');
    }
    this.answerTextDivs[this.questionPart].innerHTML = html;
  }

  check(): void {
    if (this.rightAnswerDelay) return;
    let rightAnswer = false;
    let note;
    if (typeof this.answerLetter !== 'undefined') {
      // Create note from it:
      note = new Note(this.answerLetter);
      if (typeof this.answerAccidental !== 'undefined') {
        note.accidental = this.answerAccidental;
      }

      // has this one been used before?
      let usedBefore = false;
      this.answerPartNotes.forEach((usedNote) => {
        if (note.equals(usedNote)) usedBefore = true;
      }, this);
      if (usedBefore) {
        this.feedback("You've already used that answer.");
        return;
      }

      // Check it:
      const baseMidiGuess = note.getMidiNumber() % 12;
      const baseMidiAnswer = this.questions[this.questionNumber].note.getMidiNumber() % 12;
      // eslint-disable-next-line eqeqeq
      rightAnswer = baseMidiAnswer === baseMidiGuess;
    } else {
      this.feedback('You must choose a letter.');
      return;
    }

    const skills = [18]; // Enharmonic equivalence
    skills.push(EnharmonicEquivalence.calculateSkill(note)); // Piano skill

    if (rightAnswer) {
      this.progressTracker.rightAnswer(skills, false);
      this.answerPartNotes.push(note);
      this.nextPartOrQuestion();
      this.checkMark.check(0.5);
    } else {
      this.progressTracker.wrongAnswer(skills, false);
      this.checkMark.x(0.5);
    }
  }

  nextPartOrQuestion(): void {
    this.letterPanel.clearNoteName();
    delete this.answerAccidental;
    delete this.answerLetter;
    this.answerTextDivs[this.questionPart].style.backgroundColor = 'transparent';
    if (this.questionPart === 2) {
      this.playAnswer();
      this.questionNumber++;
      if (this.questionNumber < this.maxQuestions) {
        this.rightAnswerDelay = true;
        window.setTimeout(() => this.newQuestion(), 1000);
      } else this.gameOver();
    } else {
      this.questionPart++;
      this.answerTextDivs[this.questionPart].style.backgroundColor = this.highlightColor;
    }
  }

  feedback(text): void {
    this.feedbackDiv.innerHTML = text;
    if (typeof this.timeoutID !== 'undefined') window.clearTimeout(this.timeoutID);
    this.timeoutID = window.setTimeout(() => {
      this.feedbackDiv.innerHTML = '';
    }, 5000);
  }

  gameOver(): void {
    this.instructionsDiv.innerHTML = 'Nice work!';
    this.instructionsDiv.setAttribute('role', 'alert');
    this.displayDiv.removeChild(this.lettersDiv);
    super.gameOver();
  }

  playAnswer(): void {
    this.player.play(this.note, { startTime: 0, stopTime: 1.2 });
  }

  static registry = {
    description: 'Enharmonic Equivalence',
    skills: ['double_sharps', 'double_flats', 'enharmonic_equivalence'],
  };
}

export default EnharmonicEquivalence;
